import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  Modal,
  Box,
  Typography,
  Divider,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { base_url } from "../../config/config";

const ITEMS_PER_PAGE = 20;

const ANRModal = ({ open, onClose, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [projects, setProjects] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latestProject, setLatestProject] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalProjects, setTotalProjects] = useState(0);

  const listRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  // Generate year options (last 10 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - i);

  // Month options
  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: (i + 1).toString().padStart(2, "0"),
    label: new Date(0, i).toLocaleString("de-DE", { month: "long" }),
  }));

  const fetchProjects = useCallback(
    async (pageNum = 1, isNewSearch = false) => {
      if (isNewSearch) {
        setLoading(true);
      }

      try {
        const params = new URLSearchParams({
          page: pageNum.toString(),
          limit: ITEMS_PER_PAGE.toString(),
          sortField: "createdAt",
          sortOrder: "desc",
        });

        if (searchTerm.trim()) {
          params.append("search", searchTerm.trim());
        }
        if (year) {
          params.append("year", year);
        }
        // Note: Month filtering will be done on frontend as API doesn't support it directly

        const response = await axios.get(
          `${base_url}/v1/getAllProjects?${params.toString()}`
        );

        if (response.data.status === 1) {
          let fetchedProjects = response.data.data || [];
          const total = response.data.totalProjects || fetchedProjects.length;

          // Apply month filter on frontend if needed
          if (month) {
            fetchedProjects = fetchedProjects.filter((project) => {
              const createdAt = new Date(project.createdAt);
              const createdMonth = (createdAt.getMonth() + 1)
                .toString()
                .padStart(2, "0");
              return createdMonth === month;
            });
          }

          setTotalProjects(total);

          if (isNewSearch || pageNum === 1) {
            setProjects(fetchedProjects);
            // Set latest project only on initial load without filters
            if (
              !searchTerm &&
              !month &&
              year === currentYear.toString() &&
              fetchedProjects.length > 0
            ) {
              setLatestProject(fetchedProjects[0]);
            } else if (searchTerm || month || year !== currentYear.toString()) {
              setLatestProject(null);
            }
          } else {
            setProjects((prev) => [...prev, ...fetchedProjects]);
          }

          setHasMore(fetchedProjects.length === ITEMS_PER_PAGE);
          setError(null);
        } else {
          setError("Failed to retrieve projects.");
          setHasMore(false);
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
        setError("Error while fetching projects.");
        setHasMore(false);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [searchTerm, year, month, currentYear]
  );

  // Initial load when modal opens
  useEffect(() => {
    if (open) {
      setInitialLoading(true);
      setPage(1);
      setProjects([]);
      setHasMore(true);
      fetchProjects(1, true);
    }
  }, [open]);

  // Debounced search effect
  useEffect(() => {
    if (!open) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setPage(1);
      setProjects([]);
      setHasMore(true);
      fetchProjects(1, true);
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm, year, month]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!listRef.current || loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;

    // Load more when scrolled to 80% of the list
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      const nextPage = page + 1;
      setPage(nextPage);
      setLoading(true);
      fetchProjects(nextPage, false);
    }
  }, [loading, hasMore, page, fetchProjects]);

  // Attach scroll listener
  useEffect(() => {
    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener("scroll", handleScroll);
      return () => listElement.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  const handleSelect = (project) => {
    onSelect({
      anr: project.ANR,
      auftragsbezeichnung: project.Auftragsbezeichnung,
      maxLohnaufwand: project.MaxLohnaufwand || null,
      projectId: project._id,
    });
    onClose();
  };

  const handleClose = () => {
    setSearchTerm("");
    setYear(new Date().getFullYear().toString());
    setMonth("");
    setPage(1);
    setProjects([]);
    setError(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="anr-modal-title"
      aria-describedby="anr-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "95%",
          maxWidth: "700px",
          maxHeight: "85vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="h6" id="anr-modal-title" gutterBottom>
          Select ANR
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              fullWidth
              label="Search ANR or Auftragsbezeichnung"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Year</InputLabel>
              <Select
                value={year}
                label="Select Year"
                onChange={(e) => setYear(e.target.value)}
              >
                <MenuItem value="">All Years</MenuItem>
                {yearOptions.map((yearOption) => (
                  <MenuItem key={yearOption} value={yearOption.toString()}>
                    {yearOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Month</InputLabel>
              <Select
                value={month}
                label="Select Month"
                onChange={(e) => setMonth(e.target.value)}
              >
                <MenuItem value="">All Months</MenuItem>
                {monthOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Results count */}
        {!initialLoading && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, mb: 1 }}
          >
            {totalProjects > 0
              ? `${projects.length} of ${totalProjects} projects`
              : "No projects found"}
          </Typography>
        )}

        {/* Scrollable List Container */}
        <Box
          ref={listRef}
          sx={{
            mt: 1,
            flex: 1,
            maxHeight: "400px",
            minHeight: "250px",
            overflowY: "auto",
            pr: 1,
          }}
        >
          {/* Initial Loading */}
          {initialLoading && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              my={4}
            >
              <CircularProgress size={32} />
              <Typography sx={{ ml: 2 }}>Loading projects...</Typography>
            </Box>
          )}

          {/* Error */}
          {error && !initialLoading && (
            <Box display="flex" justifyContent="center" my={3}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          {/* Latest Project Button */}
          {!initialLoading && latestProject && !searchTerm && !month && (
            <Button
              key={latestProject._id}
              variant="contained"
              fullWidth
              sx={{ mb: 1.5, py: 1.5 }}
              onClick={() => handleSelect(latestProject)}
              startIcon={<EmojiEventsIcon />}
            >
              <Box sx={{ textAlign: "left", flex: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  Latest Project: {latestProject.ANR}
                </Typography>
                <Typography variant="caption" display="block" noWrap>
                  {latestProject.Auftragsbezeichnung}
                </Typography>
              </Box>
            </Button>
          )}

          {/* Project List */}
          {!initialLoading && (
            <Grid container spacing={1}>
              {projects.map((project, index) => (
                <Grid item xs={12} key={`${project._id}-${index}`}>
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      justifyContent: "flex-start",
                      py: 1.5,
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: "action.hover",
                      },
                    }}
                    onClick={() => handleSelect(project)}
                    startIcon={<AssignmentIcon />}
                  >
                    <Box sx={{ textAlign: "left", overflow: "hidden" }}>
                      <Typography variant="body2" fontWeight="bold">
                        {project.ANR}
                      </Typography>
                      <Typography variant="caption" display="block" noWrap>
                        {project.Auftragsbezeichnung || "No description"}
                      </Typography>
                    </Box>
                  </Button>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Loading more indicator */}
          {loading && !initialLoading && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          )}

          {/* End of list message */}
          {!hasMore && projects.length > 0 && !loading && (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ py: 2 }}
            >
              No more projects to load
            </Typography>
          )}

          {/* No results */}
          {!loading && !initialLoading && projects.length === 0 && !error && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={4}
            >
              <Typography color="text.secondary">No projects found</Typography>
            </Box>
          )}
        </Box>

        {/* Close Button */}
        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ANRModal;